/* iter568 — Big DIL Hero KPI Card.
 *
 * The flagship KPI of Nutramilo. Лидира dashboard-а в Easy mode —
 * full-width hero (180-220px), не вътрешен tile. Самосъдържащ:
 *   - Зарежда /api/perinatal-dil/ceiling (личен лимит)
 *   - Зарежда /api/meals/today (за днешния DIL total)
 *   - Показва патиент-приятелски „Инсулинов бюджет" с използване, остатък,
 *     цветова урgenstone (green/amber/rose) и mode-aware единици.
 *
 * Mode-aware текст:
 *   easy     → „Инсулинов бюджет"     · 96 / 113 точки · Остават 17 точки
 *   standard → „Дневен инсулинов бюджет (DIL)" · 96 / 113 единици · 85% използвани
 *   expert   → „DIL ceiling" · 96.0 g / 113.0 g/day · 14-day avg X g
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Flame, ArrowRight, AlertCircle, TrendingDown, TrendingUp, Minus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { useDashboardMode } from "@/dashboard/DashboardModeContext";
import { DIL_TERM, dilPoints, budgetPercent, budgetUrgency } from "@/utils/dilTerminology";
import UniversalTooltip from "@/components/common/UniversalTooltip";
import PerinatalDilBadge from "@/components/profile/PerinatalDilBadge";  // iter1108

const URGENCY_THEME = {
  emerald: {
    bg: "bg-gradient-to-br from-emerald-50 via-white to-emerald-50",
    bar: "bg-emerald-500",
    text: "text-emerald-700",
    dot: "🌱",
    label_easy: "Тялото ти е спокойно днес",
    label_easy_sub: "Имаш още място за вкусна храна.",
    label_std: "Метаболитен пулс — спокоен",
    icon: TrendingDown,
  },
  amber: {
    bg: "bg-gradient-to-br from-amber-50 via-white to-amber-50",
    bar: "bg-amber-500",
    text: "text-amber-700",
    dot: "☀️",
    label_easy: "Внимавай с вечерята",
    label_easy_sub: "Тялото вече работи усилено — нека е леко.",
    label_std: "Близо до дневния връх",
    icon: Minus,
  },
  rose: {
    bg: "bg-gradient-to-br from-rose-50 via-white to-rose-50",
    bar: "bg-rose-500",
    text: "text-rose-700",
    dot: "🌙",
    label_easy: "Дай на тялото си почивка",
    label_easy_sub: "Метаболитният пулс е висок — време е за разходка или вода.",
    label_std: "Над дневния връх",
    icon: TrendingUp,
  },
};

export default function DilHeroCard() {
  const { user, API } = useAuth();
  const navigate = useNavigate();
  const { mode } = useDashboardMode();
  const T = DIL_TERM[mode] || DIL_TERM.easy;

  const [ceiling, setCeiling] = useState(null);
  const [todayDil, setTodayDil] = useState(null);
  const [weightMissing, setWeightMissing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!user) return;
    const token =
      localStorage.getItem("nutramilo_access_token") ||
      sessionStorage.getItem("nutramilo_access_token");
    if (!token) return;
    let cancelled = false;
    setLoading(true);
    const headers = { Authorization: `Bearer ${token}` };
    Promise.all([
      axios.get(`${API}/perinatal-dil/ceiling`, { headers }).catch(() => null),
      axios.get(`${API}/meals/today`, { headers }).catch(() => null),
    ]).then(([cRes, mRes]) => {
      if (cancelled) return;
      // perinatal endpoint returns either perinatal-tuned or fallback v2
      const c = cRes?.data || {};
      // Prefer ceiling_g, fallback to v2 cap if exposed
      const ceilG = c.ceiling_g || c.cap || c.ceiling || null;
      setCeiling(ceilG);
      setWeightMissing(c.weight_missing === true);
      const m = mRes?.data || {};
      const used =
        m.daily_effective_dil ?? m.daily_dil_total ?? m.daily_dil ?? null;
      setTodayDil(used);
      setLoading(false);
    }).catch(() => {
      if (cancelled) return;
      setError(true);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [user, API]);

  const data = useMemo(() => {
    const usedG = Number(todayDil) || 0;
    const totalG = Number(ceiling) || 0;
    const usedPts = dilPoints(usedG);
    const totalPts = dilPoints(totalG);
    const pct = budgetPercent(usedG, totalG);
    const urgency = budgetUrgency(pct);
    const remaining = Math.max(0, totalG - usedG);
    return { usedG, totalG, usedPts, totalPts, pct, urgency, remaining };
  }, [todayDil, ceiling]);

  if (loading) {
    return (
      <div
        data-testid="dil-hero-skeleton"
        className="rounded-3xl bg-stone-100/60 border border-stone-200 h-44 animate-pulse"
      />
    );
  }

  if (error || ceiling === null) {
    return null; // graceful — let other widgets shine
  }

  const theme = URGENCY_THEME[data.urgency] || URGENCY_THEME.emerald;
  const ThemeIcon = theme.icon;
  const isEasy = mode === "easy";

  return (
    <div
      data-testid="dil-hero-card"
      className={`relative rounded-3xl border border-stone-200 ${theme.bg} p-5 sm:p-7 overflow-hidden`}
    >
      {/* Soft background flame icon */}
      <Flame className="pointer-events-none absolute -top-2 -right-2 w-28 h-28 text-stone-100 opacity-50" />

      {/* === EASY MODE — Narrative-first (no clinical numbers in headline) === */}
      {isEasy && (
        <div className="relative">
          <div className="flex items-start gap-3 mb-4">
            <span className="text-3xl leading-none" aria-hidden="true">{theme.dot}</span>
            <div className="min-w-0">
              <h2
                className="text-xl sm:text-2xl font-bold text-stone-900 leading-tight"
                data-testid="dil-hero-narrative-title"
              >
                {theme.label_easy}
              </h2>
              <p
                className="mt-1 text-sm sm:text-[15px] text-stone-600 leading-relaxed"
                data-testid="dil-hero-narrative-sub"
              >
                {theme.label_easy_sub}
              </p>
              <div className="mt-2">
                <PerinatalDilBadge testid="dil-hero-perinatal-badge-easy" />
              </div>
            </div>
          </div>

          {/* Soft gauge — evidence, not headline */}
          <div className="mt-1 w-full h-1.5 bg-white/70 rounded-full overflow-hidden ring-1 ring-stone-200">
            <div
              data-testid="dil-hero-progress"
              className={`h-full transition-all duration-700 ${theme.bar}`}
              style={{ width: `${Math.min(100, data.pct)}%` }}
            />
          </div>
          <p className="mt-1.5 text-[11px] text-stone-500" data-testid="dil-hero-evidence">
            Това отчитаме днес от храните и движението ти.
          </p>

          {weightMissing && (
            <div
              className="mt-3 px-3 py-2 rounded-lg bg-amber-100/80 border border-amber-200 text-[11px] text-amber-900 flex items-start gap-1.5"
              data-testid="dil-hero-weight-missing"
            >
              <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>
                За по-точна картина — кажи ни колко тежиш сега.
              </span>
            </div>
          )}

          <div className="mt-5 flex flex-col sm:flex-row gap-2">
            <button
              type="button"
              onClick={() => navigate("/dashboard/diary")}
              data-testid="dil-hero-cta-meal"
              className="inline-flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-full bg-stone-900 text-white font-semibold text-sm hover:bg-stone-800 transition-colors shadow-sm"
            >
              Сподели какво яде
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* === STANDARD / EXPERT — number-aware с хуманоиден тон === */}
      {!isEasy && (
        <div className="relative flex flex-col sm:flex-row sm:items-end gap-5">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 mb-1 flex-wrap">
              <UniversalTooltip
                label={mode === "expert" ? "Дневен инсулинов товар" : "Твоят метаболитен пулс днес"}
                explanation="Колко натоварено е тялото ти от храните за деня. Изчисляваме лимит според тегло, цели и хормонален статус — целта е дневният сбор да остане спокоен."
                ctaLabel="Виж план"
                ctaPath="/dashboard/plan"
                testId="tooltip-dil-hero"
              />
              <PerinatalDilBadge testid="dil-hero-perinatal-badge-std" />
            </div>
            <div className="flex items-baseline gap-2 flex-wrap">
              <span
                className="text-4xl sm:text-5xl font-extrabold text-stone-900 leading-none"
                data-testid="dil-hero-used"
              >
                {mode === "expert" ? data.usedG.toFixed(1) : data.usedPts}
              </span>
              <span className="text-2xl sm:text-3xl font-bold text-stone-400 leading-none">/</span>
              <span
                className="text-2xl sm:text-3xl font-bold text-stone-500 leading-none"
                data-testid="dil-hero-total"
              >
                {mode === "expert" ? data.totalG.toFixed(1) : data.totalPts}
              </span>
              <span className="text-sm font-semibold text-stone-500 ml-1">
                {mode === "expert" ? "g/ден" : T.unit}
              </span>
            </div>

            <div className="mt-3 w-full h-2 bg-white/70 rounded-full overflow-hidden ring-1 ring-stone-200">
              <div
                data-testid="dil-hero-progress"
                className={`h-full transition-all duration-500 ${theme.bar}`}
                style={{ width: `${Math.min(100, data.pct)}%` }}
              />
            </div>

            <div className="mt-2 flex items-center gap-3 flex-wrap text-xs">
              <span className={`inline-flex items-center gap-1 font-semibold ${theme.text}`} data-testid="dil-hero-status">
                <ThemeIcon className="w-3.5 h-3.5" />
                {theme.label_std}
              </span>
              <span className="text-stone-500" data-testid="dil-hero-remaining">
                {mode === "expert"
                  ? `Остават: ${data.remaining.toFixed(1)} g`
                  : `Остават: ${dilPoints(data.remaining)} ${T.unit}`}
              </span>
            </div>

            {weightMissing && (
              <div
                className="mt-3 px-3 py-2 rounded-lg bg-amber-100/80 border border-amber-200 text-[11px] text-amber-900 flex items-start gap-1.5"
                data-testid="dil-hero-weight-missing"
              >
                <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>
                  Стойностите са приблизителни — въведи актуалното си тегло за прецизен резултат.
                </span>
              </div>
            )}
          </div>

          {/* Right — CTA */}
          <div className="flex flex-col items-stretch sm:items-end gap-2 shrink-0 sm:w-44">
            <button
              type="button"
              onClick={() => navigate("/dashboard/diary")}
              data-testid="dil-hero-cta-meal"
              className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full bg-stone-900 text-white font-semibold text-sm hover:bg-stone-800 transition-colors shadow-sm"
            >
              Сподели какво яде
              <ArrowRight className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => navigate("/dashboard/profile")}
              data-testid="dil-hero-cta-plan"
              className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-full text-stone-600 hover:text-stone-900 font-semibold text-xs transition-colors"
            >
              Промени дневната ми цел
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
